import React, { useEffect, useMemo, useState } from "react";
import "../../styles/ApiDocumentationPage.css";

const GUIDE_ITEMS = [
  ["overview", "Overview"],
  ["quickstart", "Quickstart"],
  ["authentication", "Authentication"],
  ["scopes", "Scopes and Access"],
  ["pagination", "Pagination"],
  ["errors", "Errors"],
];

const ENDPOINTS = [
  { id: "test-token", group: "General", method: "GET", path: "/api/external/v1/test", title: "Test an API token", permission: "Authenticated token", description: "Verify a token and inspect its effective tag access, detailed scopes, and expiration.", response: { status: "ok", token: { name: "Integration name", tag_access: "incident_violation", resources: ["incidents", "violations"], scopes: ["incidents:read", "violations:read"], read: true, write: false, expires_at: null } } },
  { id: "list-incidents", group: "Incidents", method: "GET", path: "/api/external/v1/incidents", title: "List incidents", permission: "incidents:read", description: "Return incident records in reverse chronological order.", query: [["limit", "integer", "No", "Records to return. Default 50; maximum 200."], ["offset", "integer", "No", "Records to skip. Default 0."], ["user_id", "integer", "No", "Only return records for this employee ID."]], response: { data: [{ inc_id: 42, inc_userid: 123, employee_name: "Example Employee", inc_number: "ACC-2026-8-1-NWTDOL-2026-08-26", inc_type: "Incident", inc_date: "2026-08-26" }], pagination: { limit: 50, offset: 0, returned: 1 } } },
  { id: "get-incident", group: "Incidents", method: "GET", path: "/api/external/v1/incidents/{id}", title: "Get an incident", permission: "incidents:read", description: "Return one incident by its incident ID.", pathParams: [["id", "integer", "Incident ID"]], response: { data: { inc_id: 42, inc_userid: 123, inc_number: "ACC-2026-8-1-NWTDOL-2026-08-26", inc_type: "Incident", inc_date: "2026-08-26", inc_description: "Example description" } } },
  { id: "create-incident", group: "Incidents", method: "POST", path: "/api/external/v1/incidents", title: "Create an incident", permission: "incidents:write", description: "Create an incident. The API generates its incident number and audit fields.", required: "inc_userid, inc_type, inc_date", body: { inc_userid: 123, inc_type: "Incident", inc_date: "2026-08-26", inc_description: "Created by an approved integration", inc_truck_number: "TRK-100" }, response: { data: { inc_id: 43, inc_number: "ACC-2026-8-2-NWTDOL-2026-08-26" } } },
  { id: "update-incident", group: "Incidents", method: "PUT", path: "/api/external/v1/incidents/{id}", title: "Update an incident", permission: "incidents:write", description: "Update writable fields on an existing incident. The employee ID cannot be changed.", pathParams: [["id", "integer", "Incident ID"]], body: { inc_description: "Updated description", inc_truck_number: "TRK-101" }, response: { message: "Incident updated." } },
  { id: "list-violations", group: "Violations", method: "GET", path: "/api/external/v1/violations", title: "List violations", permission: "violations:read", description: "Return violation records in reverse chronological order.", query: [["limit", "integer", "No", "Records to return. Default 50; maximum 200."], ["offset", "integer", "No", "Records to skip. Default 0."], ["user_id", "integer", "No", "Only return records for this employee ID."]], response: { data: [{ viol_id: 17, viol_userid: 123, employee_name: "Example Employee", viol_number: "POLICYV-2026-8-1-NWTDOL-2026-08-26", viol_type: "Policy", viol_date: "2026-08-26" }], pagination: { limit: 50, offset: 0, returned: 1 } } },
  { id: "get-violation", group: "Violations", method: "GET", path: "/api/external/v1/violations/{id}", title: "Get a violation", permission: "violations:read", description: "Return one violation by its violation ID.", pathParams: [["id", "integer", "Violation ID"]], response: { data: { viol_id: 17, viol_userid: 123, viol_number: "POLICYV-2026-8-1-NWTDOL-2026-08-26", viol_type: "Policy", viol_date: "2026-08-26" } } },
  { id: "create-violation", group: "Violations", method: "POST", path: "/api/external/v1/violations", title: "Create a violation", permission: "violations:write", description: "Create a violation. The API generates its violation number and audit fields.", required: "viol_userid, viol_date", body: { viol_userid: 123, viol_type: "Policy", viol_date: "2026-08-26", viol_truck_number: "TRK-100" }, response: { data: { viol_id: 18, viol_number: "POLICYV-2026-8-2-NWTDOL-2026-08-26" } } },
  { id: "update-violation", group: "Violations", method: "PUT", path: "/api/external/v1/violations/{id}", title: "Update a violation", permission: "violations:write", description: "Update writable fields on an existing violation. The employee ID cannot be changed.", pathParams: [["id", "integer", "Violation ID"]], body: { viol_type: "Updated Policy", viol_truck_number: "TRK-101" }, response: { message: "Violation updated." } },
];

const JSONText = ({ value }) => <pre>{JSON.stringify(value, null, 2)}</pre>;

const ApiDocumentationPage = () => {
  const [selected, setSelected] = useState("overview");
  const [language, setLanguage] = useState("python");
  const [copied, setCopied] = useState("");
  const [apiToken, setApiToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [requestUrl, setRequestUrl] = useState("");
  const [requestBody, setRequestBody] = useState("");
  const [requestResult, setRequestResult] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || window.location.origin).replace(/\/$/, "");
  const endpoint = ENDPOINTS.find((item) => item.id === selected);
  const endpointGroups = useMemo(() => [...new Set(ENDPOINTS.map((item) => item.group))], []);

  useEffect(() => {
    if (!endpoint) return;
    setRequestUrl(`${baseUrl}${endpoint.path.replace("{id}", "42")}`);
    setRequestBody(endpoint.body ? JSON.stringify(endpoint.body, null, 2) : "");
    setRequestResult(null);
  }, [baseUrl, endpoint]);

  const example = endpoint
    ? language === "python"
      ? `import requests\n\nurl = "${baseUrl}${endpoint.path.replace("{id}", "42")}"\nheaders = {"Authorization": "Bearer YOUR_API_TOKEN"${endpoint.body ? ', "Content-Type": "application/json"' : ""}}\n${endpoint.body ? `payload = ${JSON.stringify(endpoint.body, null, 2)}\n\nresponse = requests.${endpoint.method.toLowerCase()}(url, headers=headers, json=payload)` : `response = requests.get(url, headers=headers)`}\nresponse.raise_for_status()\nprint(response.json())`
      : `curl --request ${endpoint.method} \\\n  --url '${baseUrl}${endpoint.path.replace("{id}", "42")}' \\\n  --header 'Authorization: Bearer YOUR_API_TOKEN'${endpoint.body ? ` \\\n  --header 'Content-Type: application/json' \\\n  --data '${JSON.stringify(endpoint.body)}'` : ""}`
    : "";

  const copy = async (text, key) => {
    await navigator.clipboard.writeText(text);
    setCopied(key);
    window.setTimeout(() => setCopied(""), 1600);
  };

  const tryRequest = async (event) => {
    event.preventDefault();
    if (!endpoint || !apiToken.trim() || !requestUrl.trim()) return;
    if (["POST", "PUT"].includes(endpoint.method) && !window.confirm(`Send this ${endpoint.method} request? It may change live data.`)) return;

    setRequesting(true);
    setRequestResult(null);
    try {
      const options = {
        method: endpoint.method,
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${apiToken.trim()}`,
        },
      };
      if (endpoint.body) {
        options.headers["Content-Type"] = "application/json";
        options.body = requestBody;
      }
      const response = await fetch(requestUrl.trim(), options);
      const text = await response.text();
      let data = text;
      try { data = text ? JSON.parse(text) : null; } catch (_) { /* Keep non-JSON response text. */ }
      setRequestResult({ ok: response.ok, status: response.status, statusText: response.statusText, data });
    } catch (error) {
      setRequestResult({ ok: false, status: 0, statusText: "Request failed", data: error.message });
    } finally {
      setRequesting(false);
    }
  };

  const Guide = () => {
    if (selected === "overview") return <><h1>REST API Overview</h1><p className="api-doc-lead">Use the REST API to connect approved external applications to incident and violation data while controlling every integration with a separate token and detailed permissions.</p><div className="api-doc-card-grid"><button onClick={() => setSelected("quickstart")}><i className="fas fa-rocket" /><h2>Getting Started</h2><p>Create a token, test authentication, and make your first request.</p></button><button onClick={() => setSelected("authentication")}><i className="fas fa-key" /><h2>Authentication</h2><p>Learn how bearer tokens are sent and secured.</p></button><button onClick={() => setSelected("scopes")}><i className="fas fa-shield-alt" /><h2>Scopes</h2><p>Understand read/write permissions for each resource.</p></button><button onClick={() => setSelected("errors")}><i className="fas fa-exclamation-circle" /><h2>Errors</h2><p>Handle HTTP status codes and structured API errors.</p></button></div><div className="api-doc-callout"><strong>API base URL</strong><code>{baseUrl}/api/external/v1</code></div></>;
    if (selected === "quickstart") return <><h1>Quickstart</h1><ol className="api-doc-steps"><li><b>Create an API token.</b><span>Go to Admin Settings - API Tokens and select the minimum required Tag Access and scopes.</span></li><li><b>Copy the token.</b><span>The full secret is displayed once. Store it in a secret manager or protected environment variable.</span></li><li><b>Test authentication.</b><span>Call <code>GET /api/external/v1/test</code> and confirm the returned scopes.</span></li><li><b>Call a resource endpoint.</b><span>Use an Incidents or Violations endpoint allowed by those scopes.</span></li></ol><div className="api-doc-callout warning"><strong>Do not expose tokens</strong><span>Never place an API token in browser code, a public repository, screenshots, email, or application logs.</span></div></>;
    if (selected === "authentication") return <><h1>Authentication</h1><p>Every request requires the token in the HTTP <code>Authorization</code> header using the Bearer scheme.</p><JSONText value={{ Authorization: "Bearer YOUR_API_TOKEN", Accept: "application/json" }} /><h2>Token lifecycle</h2><ul><li>Edit changes name, tag access, scopes, or expiration without changing the secret.</li><li>Regenerate immediately invalidates the previous secret.</li><li>Delete immediately removes access.</li><li>An expired token returns HTTP 401.</li></ul></>;
    if (selected === "scopes") return <><h1>Scopes and Tag Access</h1><p>Tag Access limits which resources a token can reach. Detailed scopes then control the allowed operation.</p><table className="api-doc-table"><thead><tr><th>Scope</th><th>Allows</th></tr></thead><tbody><tr><td><code>incidents:read</code></td><td>List incidents and read one incident.</td></tr><tr><td><code>incidents:write</code></td><td>Create and update incidents.</td></tr><tr><td><code>violations:read</code></td><td>List violations and read one violation.</td></tr><tr><td><code>violations:write</code></td><td>Create and update violations.</td></tr></tbody></table><h2>Tag defaults</h2><ul><li><b>Entire Organization:</b> all four scopes.</li><li><b>Incident and Violation:</b> both read scopes.</li><li><b>Incident:</b> incident read.</li><li><b>Violation:</b> violation read.</li></ul><p>Administrators may adjust detailed scopes after choosing the tag.</p></>;
    if (selected === "pagination") return <><h1>Pagination</h1><p>List endpoints use offset pagination.</p><table className="api-doc-table"><thead><tr><th>Parameter</th><th>Default</th><th>Rules</th></tr></thead><tbody><tr><td><code>limit</code></td><td>50</td><td>1-200 records.</td></tr><tr><td><code>offset</code></td><td>0</td><td>Number of records to skip.</td></tr></tbody></table><JSONText value={{ data: ["...records"], pagination: { limit: 50, offset: 0, returned: 50 } }} /><p>Request the next page by adding the current limit to the offset. Stop when <code>returned</code> is less than <code>limit</code>.</p></>;
    return <><h1>Errors</h1><p>Errors use an HTTP status and a JSON body containing an error code and message.</p><table className="api-doc-table"><thead><tr><th>Status</th><th>Meaning</th></tr></thead><tbody><tr><td>400</td><td>Invalid parameters, missing fields, or an invalid request body.</td></tr><tr><td>401</td><td>Missing, invalid, deleted, or expired token.</td></tr><tr><td>403</td><td>The token lacks the resource or operation scope.</td></tr><tr><td>404</td><td>The requested record does not exist.</td></tr><tr><td>500</td><td>An unexpected server or database error occurred.</td></tr></tbody></table><JSONText value={{ error: "insufficient_scope", message: "This token does not have write access to incidents." }} /></>;
  };

  return (
    <div className="api-doc-page">
      <header className="api-doc-portal-header">
        <div className="api-doc-brand">
          <span className="api-doc-brand-icon" aria-hidden="true">
            <i className="fas fa-code" />
          </span>
          <div>
            <span>Developer Portal</span>
            <h1>REST API Documentation</h1>
          </div>
        </div>
        <div className="api-doc-portal-actions">
          <span className="api-doc-version">API v1</span>
        </div>
      </header>

      <section className="api-doc-workspace">
        <div className="api-doc-layout">
          <aside className="api-doc-sidebar">
            <div className="api-doc-sidebar-title">
              <i className="fas fa-book-open" aria-hidden="true" />
              <span>Developer Guides</span>
            </div>
            <nav aria-label="API documentation sections">
              <span className="api-doc-nav-label">Guides</span>
              {GUIDE_ITEMS.map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  data-no-standard-button="true"
                  className={selected === id ? "active" : ""}
                  onClick={() => setSelected(id)}
                >
                  <span>{label}</span>
                </button>
              ))}
              {endpointGroups.map((group) => (
                <React.Fragment key={group}>
                  <span className="api-doc-nav-label">{group}</span>
                  {ENDPOINTS.filter((item) => item.group === group).map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      data-no-standard-button="true"
                      className={selected === item.id ? "active" : ""}
                      onClick={() => setSelected(item.id)}
                    >
                      <span>{item.title}</span>
                      <b className={`method ${item.method.toLowerCase()}`}>{item.method}</b>
                    </button>
                  ))}
                </React.Fragment>
              ))}
            </nav>
          </aside>

          <main className="api-doc-content">
            {endpoint ? (
              <>
                <div className="api-doc-endpoint-heading">
                  <div>
                    <div className="api-doc-path">
                      <span className={`method ${endpoint.method.toLowerCase()}`}>{endpoint.method}</span>
                      <code>{endpoint.path}</code>
                    </div>
                    <h1>{endpoint.title}</h1>
                    <p className="api-doc-lead">{endpoint.description}</p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-primary api-doc-copy-url"
                    onClick={() => copy(`${baseUrl}${endpoint.path}`, "page")}
                  >
                    <i className={copied === "page" ? "fas fa-check" : "far fa-copy"} aria-hidden="true" />
                    {copied === "page" ? "Copied" : "Copy URL"}
                  </button>
                </div>

                <div className="api-doc-endpoint-layout">
                  <article className="api-doc-reference">
                    <div className="api-doc-requirement">
                      <span>Required scope</span>
                      <code>{endpoint.permission}</code>
                    </div>
                    {endpoint.required && (
                      <div className="api-doc-callout warning">
                        <strong>Required body fields</strong>
                        <code>{endpoint.required}</code>
                      </div>
                    )}
                    {endpoint.pathParams && (
                      <section className="api-doc-section">
                        <h2>Path parameters</h2>
                        <ParamTable rows={endpoint.pathParams} path />
                      </section>
                    )}
                    {endpoint.query && (
                      <section className="api-doc-section">
                        <h2>Query parameters</h2>
                        <ParamTable rows={endpoint.query} />
                      </section>
                    )}
                    {endpoint.body && (
                      <section className="api-doc-section">
                        <h2>Request body example</h2>
                        <JSONText value={endpoint.body} />
                      </section>
                    )}
                    <section className="api-doc-section">
                      <h2>Response example</h2>
                      <JSONText value={endpoint.response} />
                    </section>
                  </article>

                <aside className="api-doc-example-panel">
                  <div className="api-doc-example-heading">
                    <div>
                      <span>API request console</span>
                      <small>Enter a token and send a request to this endpoint.</small>
                    </div>
                      <button type="button" onClick={() => copy(example, "example")}>
                        <i className={copied === "example" ? "fas fa-check" : "far fa-copy"} aria-hidden="true" />
                        {copied === "example" ? "Copied" : "Copy"}
                      </button>
                    </div>
                    <form className="api-doc-try-form" onSubmit={tryRequest} autoComplete="off">
                      <label htmlFor="api-doc-token">Credentials</label>
                      <div className="api-doc-token-input">
                        <span>Bearer</span>
                        <input
                          id="api-doc-token"
                          type={showToken ? "text" : "password"}
                          value={apiToken}
                          onChange={(event) => setApiToken(event.target.value)}
                          placeholder="Paste API token"
                          required
                          spellCheck="false"
                          autoCapitalize="none"
                        />
                        <button type="button" aria-label={showToken ? "Hide API token" : "Show API token"} onClick={() => setShowToken((value) => !value)}>
                          <i className={showToken ? "far fa-eye-slash" : "far fa-eye"} aria-hidden="true" />
                        </button>
                      </div>
                      <label htmlFor="api-doc-request-url">URL</label>
                      <div className="api-doc-url-input">
                        <span>{endpoint.method}</span>
                        <input id="api-doc-request-url" type="url" value={requestUrl} onChange={(event) => setRequestUrl(event.target.value)} required spellCheck="false" />
                      </div>
                      {endpoint.body && <><label htmlFor="api-doc-request-body">JSON request body</label><textarea id="api-doc-request-body" value={requestBody} onChange={(event) => setRequestBody(event.target.value)} rows="7" spellCheck="false" required /></>}
                    </form>
                    <div className="api-doc-code-tabs">
                      <button
                        type="button"
                        className={language === "python" ? "active" : ""}
                        onClick={() => setLanguage("python")}
                      >
                        <i className="fab fa-python" aria-hidden="true" /> Python
                      </button>
                      <button
                        type="button"
                        className={language === "curl" ? "active" : ""}
                        onClick={() => setLanguage("curl")}
                      >
                        <i className="fas fa-terminal" aria-hidden="true" /> cURL
                      </button>
                    </div>
                    <pre>{example}</pre>
                    <div className="api-doc-try-actions">
                      <span><i className="fas fa-lock" aria-hidden="true" /> Token is not saved.</span>
                      <button type="button" disabled={requesting || !apiToken.trim() || !requestUrl.trim()} onClick={tryRequest}>
                        {requesting ? <><i className="fas fa-spinner fa-spin" aria-hidden="true" /> Sending...</> : <>Try It <i className="fas fa-paper-plane" aria-hidden="true" /></>}
                      </button>
                    </div>
                    {requestResult && <div className="api-doc-response"><div><strong>Response</strong><span className={requestResult.ok ? "success" : "error"}>{requestResult.status || "Network"} {requestResult.statusText}</span></div><pre>{typeof requestResult.data === "string" ? requestResult.data : JSON.stringify(requestResult.data, null, 2)}</pre></div>}
                  </aside>
                </div>
              </>
            ) : (
              <Guide />
            )}
          </main>
        </div>
      </section>
    </div>
  );
};

const ParamTable = ({ rows, path = false }) => <table className="api-doc-table"><thead><tr><th>Name</th><th>Type</th>{!path && <th>Required</th>}<th>Description</th></tr></thead><tbody>{rows.map((row) => <tr key={row[0]}><td><code>{row[0]}</code></td><td>{row[1]}</td>{!path && <td>{row[2]}</td>}<td>{row[path ? 2 : 3]}</td></tr>)}</tbody></table>;

export default ApiDocumentationPage;
